import { useEffect, useRef, useState } from 'preact/hooks';
import { toBase64 } from '../../core/bytes/encoding';
import { downloadBlob } from '../download';
import { fmtBytes } from '../format';
import { HexView } from './HexView';
import { Icon, type IconName } from './Icon';

/** One exportable representation of something: text (PEM, JSON, …) or bytes (DER, TLS structures). */
export interface ExportFormat {
  label: string;
  filename: string;
  mime?: string;
  /** Text content, or a producer of it (evaluated on demand). */
  text?: string | (() => string);
  /** Binary content, or a producer of it. Copying a binary format copies its base64. */
  bytes?: Uint8Array | (() => Uint8Array);
  hint?: string;
}

const resolve = <T,>(v: T | (() => T)): T => (typeof v === 'function' ? (v as () => T)() : v);
const contentOf = (f: ExportFormat): string | Uint8Array => (f.text !== undefined ? resolve(f.text) : resolve(f.bytes!));
const copyTextOf = (f: ExportFormat): string => (f.text !== undefined ? resolve(f.text) : toBase64(resolve(f.bytes!)));

async function copyToClipboard(s: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(s);
    return true;
  } catch {
    return false;
  }
}

/**
 * "Export ▾": every format can be viewed in place, copied to the clipboard, or downloaded — the
 * user chooses; nothing is downloaded unasked.
 */
export function ExportMenu({ label = 'Export', icon = 'download', formats, ghost = false, title }: { label?: string; icon?: IconName; formats: ExportFormat[]; ghost?: boolean; title?: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<number>();
  const [viewing, setViewing] = useState<ExportFormat>();
  const root = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const copy = async (f: ExportFormat, i: number) => {
    if (await copyToClipboard(copyTextOf(f))) { setCopied(i); setTimeout(() => setCopied(undefined), 1200); }
  };
  const download = (f: ExportFormat) => {
    downloadBlob(f.filename, contentOf(f), f.mime ?? (f.text !== undefined ? 'text/plain' : 'application/octet-stream'));
    setOpen(false);
  };
  return (
    <span class="export" ref={root}>
      <button type="button" class={`btn btn-sm ${ghost ? 'btn-ghost' : ''}`} onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open} title={title ?? 'View, copy or download'}>
        <Icon name={icon} /> {label} <Icon name="chevronDown" size={12} class="chev" />
      </button>
      {open && (
        <div class="export-menu" role="menu">
          {formats.map((f, i) => (
            <div key={f.label} class="export-row" role="menuitem">
              <span class="export-name"><span>{f.label}</span>{f.hint && <span class="tiny muted">{f.hint}</span>}</span>
              <span class="export-actions">
                <button type="button" class="btn-icon" title="View" onClick={() => { setViewing(f); setOpen(false); }}><Icon name="eye" /></button>
                <button type="button" class="btn-icon" title={f.text !== undefined ? 'Copy' : 'Copy as base64'} onClick={() => void copy(f, i)}><Icon name={copied === i ? 'check' : 'copy'} /></button>
                <button type="button" class="btn-icon" title={`Download ${f.filename}`} onClick={() => download(f)}><Icon name="download" /></button>
              </span>
            </div>
          ))}
        </div>
      )}
      {viewing && <Viewer format={viewing} onClose={() => setViewing(undefined)} />}
    </span>
  );
}

/** Modal showing a format's content: text as is, bytes as base64 plus a hex dump. */
function Viewer({ format, onClose }: { format: ExportFormat; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [copied, setCopied] = useState(false);
  const content = contentOf(format);
  const isText = typeof content === 'string';
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    d.showModal();
    const onCancel = (e: Event) => { e.preventDefault(); onClose(); };
    d.addEventListener('cancel', onCancel);
    return () => { d.removeEventListener('cancel', onCancel); if (d.open) d.close(); };
  }, []);
  const copy = async () => {
    if (await copyToClipboard(copyTextOf(format))) { setCopied(true); setTimeout(() => setCopied(false), 1200); }
  };
  return (
    <dialog ref={ref} class="viewer" onClick={(e) => { if (e.target === ref.current) onClose(); }}>
      <div class="viewer-head">
        <div>
          <strong>{format.label}</strong>
          <div class="tiny muted">{format.filename} · {fmtBytes(isText ? new TextEncoder().encode(content).length : content.length)}{format.hint ? ` · ${format.hint}` : ''}</div>
        </div>
        <button type="button" class="btn-icon" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
      </div>
      <div class="viewer-body">
        {isText ? (
          <pre class="raw wrap">{content}</pre>
        ) : (
          <>
            <div class="tiny muted" style="margin-bottom:0.3rem">base64</div>
            <pre class="raw wrap">{toBase64(content)}</pre>
            <div class="tiny muted" style="margin:0.6rem 0 0.3rem">hex</div>
            <HexView data={content} />
          </>
        )}
      </div>
      <div class="viewer-foot">
        <button type="button" class="btn btn-sm" onClick={() => void copy()}><Icon name={copied ? 'check' : 'copy'} /> {copied ? 'Copied' : isText ? 'Copy' : 'Copy base64'}</button>
        <button type="button" class="btn btn-sm" onClick={() => downloadBlob(format.filename, content, format.mime ?? (isText ? 'text/plain' : 'application/octet-stream'))}><Icon name="download" /> Download {format.filename}</button>
        <button type="button" class="btn btn-sm btn-ghost right" onClick={onClose}>Close</button>
      </div>
    </dialog>
  );
}
